import { isMaxBusinessStatusAuthorized } from './maxBusinessStatus'
import { isSmsGateStatusAuthorized } from './smsGateStatus'

interface IAuthStatusResult {
    data: unknown;
    error: unknown;
}

interface IAuthStatusApiClient {
    networkFixer: {
        getAuthStatusRework: (params: { connectorId: string }) => Promise<IAuthStatusResult>;
        getMaxBusinessStatus?: (params: { connectorId: string }) => Promise<IAuthStatusResult>;
        getSmsGateStatus?: (params: { connectorId: string }) => Promise<IAuthStatusResult>;
    };
}

interface IFetchReworkAuthStatusOptions {
    apiClient?: IAuthStatusApiClient;
    hasStatus?: (connectorId: string) => boolean;
    isLoading?: (connectorId: string) => boolean;
    setLoading: (connectorId: string, value: boolean) => void;
    setStatus: (connectorId: string, value: boolean | null) => void;
    timeoutMs?: number;
}

export const REWORK_AUTH_STATUS_DEADLINE_MS = 5_000;

function withDeadline<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(
            () => reject(new Error("auth status deadline exceeded")),
            timeoutMs,
        );

        promise.then(
            (value) => {
                clearTimeout(timeout);
                resolve(value);
            },
            (error) => {
                clearTimeout(timeout);
                reject(error);
            },
        );
    });
}

async function fetchAuthStatusByConnectorId(
    connectorId: string | null,
    {
        setStatus,
        setLoading,
        isLoading,
        hasStatus,
        timeoutMs = REWORK_AUTH_STATUS_DEADLINE_MS,
    }: IFetchReworkAuthStatusOptions,
    loadAuthorized: (connectorId: string) => Promise<boolean | null>,
): Promise<void> {
    if (!connectorId) {
        return;
    }
    if (isLoading?.(connectorId) || hasStatus?.(connectorId)) {
        return;
    }

    setLoading(connectorId, true);

    try {
        const authorized = await withDeadline(loadAuthorized(connectorId), timeoutMs);
        setStatus(connectorId, authorized);
    } catch {
        setStatus(connectorId, null);
    } finally {
        setLoading(connectorId, false);
    }
}

export async function fetchReworkAuthStatusByConnectorId(
    connectorId: string | null,
    options: IFetchReworkAuthStatusOptions,
): Promise<void> {
    const apiClient = options.apiClient;
    if (!connectorId || !apiClient) {
        return;
    }

    return fetchAuthStatusByConnectorId(connectorId, options, async (id) => {
        const { data: authData, error: authError } = await apiClient.networkFixer.getAuthStatusRework({
            connectorId: id,
        });
        if (authError) {
            return null;
        }
        return authData === "authorized";
    });
}

export async function fetchMaxBusinessAuthStatusByConnectorId(
    connectorId: string | null,
    options: IFetchReworkAuthStatusOptions,
): Promise<void> {
    const apiClient = options.apiClient;
    if (!connectorId || !apiClient?.networkFixer.getMaxBusinessStatus) {
        return;
    }

    return fetchAuthStatusByConnectorId(connectorId, options, async (id) => {
        const statusResponse = await apiClient.networkFixer.getMaxBusinessStatus!({
            connectorId: id,
        });
        return isMaxBusinessStatusAuthorized(statusResponse);
    });
}

export async function fetchSmsGateAuthStatusByConnectorId(
    connectorId: string | null,
    options: IFetchReworkAuthStatusOptions,
): Promise<void> {
    const apiClient = options.apiClient;
    if (!connectorId || !apiClient?.networkFixer.getSmsGateStatus) {
        return;
    }

    return fetchAuthStatusByConnectorId(connectorId, options, async (id) => {
        const statusResponse = await apiClient.networkFixer.getSmsGateStatus!({
            connectorId: id,
        });
        return isSmsGateStatusAuthorized(statusResponse);
    });
}
